import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createApplication } from '../../api/applications';
import { getAvailabilitySlots } from '../../api/availability';
import { createSchedule } from '../../api/schedules';
import { listQuestionSets } from '../../api/questionSets';
import { listMyOrgs } from '../../api/orgs';

function todayIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatSlotLabel(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// ScheduleForm
// ---------------------------------------------------------------------------
//
// Shared "create scheduled interview" form. Reused by:
//   - SchedulesPage  (collapsible panel)
//   - InterviewsPage (toolbar action)
//
// Props:
//   onSuccess(payload) - called after success: ScheduleResponse for SUPERVISED,
//     or CandidateApplicationDto (plus hostEmail for list refresh) for AUTONOMOUS
//   onCancel()             - called when the user clicks "Cancel"
//   defaultHostEmail       - optional email to pre-fill the host email field
//   defaultHostName        - optional name to pre-fill the host name field
//
// Notes:
//   - SUPERVISED (default): POST /api/schedules — human-led session.
//   - AUTONOMOUS: POST /api/applications — AI-led first round (ApplicationServiceImpl).
//   - Backend overrides hostEmail/hostName from the JWT for schedules, but we still send
//     them so older API clients keep working.
// ---------------------------------------------------------------------------

const INTERVIEW_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'ja', label: 'Japanese' },
  { code: 'hi', label: 'Hindi' },
  { code: 'mr', label: 'Marathi' },
];

const EMPTY_FORM = {
  sessionType:     'SUPERVISED',
  title:           '',
  hostName:        '',
  hostEmail:       '',
  candidateName:   '',
  candidateEmail:  '',
  jobTitle:        '',
  scheduledAt:     '',
  durationMinutes: 60,
  language:        'en',
  questionSetId:   '',
  codingRoundEnabled: false,
  codingDurationMinutes: 30,
};

export default function ScheduleForm({
  onSuccess,
  onCancel,
  defaultHostEmail = '',
  defaultHostName  = '',
}) {
  const [form,    setForm]    = useState({
    ...EMPTY_FORM,
    hostEmail: defaultHostEmail,
    hostName:  defaultHostName,
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [questionSets, setQuestionSets] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [scheduleDate, setScheduleDate] = useState(todayIsoDate);
  const [selectedSlotIso, setSelectedSlotIso] = useState('');
  const [availabilityConfigured, setAvailabilityConfigured] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const effectiveHostEmail = useMemo(() => {
    if (form.sessionType === 'AUTONOMOUS') {
      return (defaultHostEmail || '').trim();
    }
    return (form.hostEmail || defaultHostEmail || '').trim();
  }, [form.sessionType, form.hostEmail, defaultHostEmail]);

  // Load available question sets once - silent failure (the field is optional).
  useEffect(() => {
    let cancelled = false;
    listMyOrgs().then((res) => {
      if (!cancelled) {
        const orgs = Array.isArray(res.data) ? res.data : [];
        setOrganizations(orgs);
        if (orgs.length === 1) setSelectedOrgId(orgs[0].id);
      }
    }).catch((err) => console.warn('[ScheduleForm] organizations unavailable:', err.message));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res  = await listQuestionSets();
        const list = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
        if (!cancelled) setQuestionSets(list);
      } catch (err) {
        console.warn('[ScheduleForm] listQuestionSets failed (optional):', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!effectiveHostEmail || !scheduleDate) {
      setAvailabilityConfigured(null);
      setAvailableSlots([]);
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    (async () => {
      try {
        const res = await getAvailabilitySlots(effectiveHostEmail, scheduleDate);
        if (cancelled) return;
        const configured = !!res.data?.configured;
        const slots = Array.isArray(res.data?.slots) ? res.data.slots : [];
        setAvailabilityConfigured(configured);
        setAvailableSlots(slots);
        setSelectedSlotIso('');
        if (!configured) {
          setForm((prev) => ({ ...prev, scheduledAt: '' }));
        }
      } catch (err) {
        console.warn('[ScheduleForm] getAvailabilitySlots failed, using manual picker:', err.message);
        if (!cancelled) {
          setAvailabilityConfigured(false);
          setAvailableSlots([]);
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveHostEmail, scheduleDate]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');

    const isAutonomous = form.sessionType === 'AUTONOMOUS';

    // ── Validation ────────────────────────────────────────────────────────
    if (!form.candidateEmail.trim()) {
      setError('Candidate email is required.');
      return;
    }
    const useSlotPicker = availabilityConfigured === true;
    if (useSlotPicker) {
      if (!selectedSlotIso) {
        setError('Select an open time slot.');
        return;
      }
    } else if (!form.scheduledAt) {
      setError('Scheduled date/time is required.');
      return;
    }
    if (isAutonomous) {
      if (!selectedOrgId) {
        setError('Select an organization for the AI interview.');
        return;
      }
      if (!form.jobTitle.trim()) {
        setError('Job title is required for an AI-led (autonomous) interview.');
        return;
      }
      if (!form.questionSetId) {
        setError('Question set is required for an AI-led (autonomous) interview.');
        return;
      }
    }

    // ── ISO conversion ────────────────────────────────────────────────────
    let scheduledAtIso;
    try {
      scheduledAtIso = useSlotPicker
        ? new Date(selectedSlotIso).toISOString()
        : new Date(form.scheduledAt).toISOString();
    } catch {
      setError('Invalid scheduled date/time.');
      return;
    }

    console.log('[ScheduleForm] submitting', { sessionType: form.sessionType, isAutonomous });
    setLoading(true);

    try {
      if (isAutonomous) {
        const appPayload = {
          candidateEmail: form.candidateEmail.trim(),
          candidateName:  form.candidateName.trim() || undefined,
          jobTitle:       form.jobTitle.trim(),
          scheduledAt:    scheduledAtIso,
          // CR-011: AI-led interviews previously had no duration field at all and
          // silently inherited the entity default of 60. Send it explicitly.
          durationMinutes: Number(form.durationMinutes) || 60,
          language:         form.language || 'en',
          questionSetId:  form.questionSetId,
          codingRoundEnabled: form.codingRoundEnabled,
          codingDurationMinutes: Number(form.codingDurationMinutes),
        };
        const res = await createApplication(appPayload, selectedOrgId);
        console.log('[ScheduleForm] created application id=', res?.data?.id);
        setForm({
          ...EMPTY_FORM,
          hostEmail: defaultHostEmail,
          hostName:  defaultHostName,
        });
        setSelectedSlotIso('');
        setScheduleDate(todayIsoDate());
        const hostEmail = (form.hostEmail || defaultHostEmail || '').trim();
        onSuccess?.({ ...res.data, hostEmail });
      } else {
        const payload = {
          title:           form.title.trim() || undefined,
          hostName:        form.hostName.trim() || undefined,
          hostEmail:       form.hostEmail.trim() || undefined,
          candidateName:   form.candidateName.trim() || undefined,
          candidateEmail:  form.candidateEmail.trim(),
          scheduledAt:     scheduledAtIso,
          durationMinutes: Number(form.durationMinutes) || 60,
          language:        form.language || 'en',
          questionSetId:   form.questionSetId || undefined,
        };
        const res = await createSchedule(payload);
        console.log('[ScheduleForm] created schedule id=', res?.data?.id);
        setForm({
          ...EMPTY_FORM,
          hostEmail: defaultHostEmail,
          hostName:  defaultHostName,
        });
        setSelectedSlotIso('');
        setScheduleDate(todayIsoDate());
        onSuccess?.(res.data);
      }
    } catch (err) {
      console.error('[ScheduleForm] submit failed:', err.message);
      const msg =
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || (isAutonomous ? 'Failed to create application.' : 'Failed to schedule interview.');
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, [form, defaultHostEmail, defaultHostName, onSuccess, availabilityConfigured, selectedSlotIso]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="card p-6">
      <h2 className="text-sm font-semibold text-slate-900 mb-5">
        New scheduled interview
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">

        {form.sessionType === 'AUTONOMOUS' && <label className="block text-sm font-medium text-slate-700">Organization *
          <select className="input w-full mt-1" value={selectedOrgId} onChange={(e) => { setSelectedOrgId(e.target.value); handleChange('questionSetId', ''); }} required>
            <option value="">Select organization…</option>
            {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
        </label>}

        <div>
          <label htmlFor="schedule-session-type" className="block text-sm font-medium text-slate-700 mb-1.5">
            Session type
          </label>
          <select
            id="schedule-session-type"
            value={form.sessionType}
            onChange={(e) => handleChange('sessionType', e.target.value)}
            className="input w-full"
          >
            <option value="SUPERVISED">SUPERVISED — Human-led interview</option>
            <option value="AUTONOMOUS">AUTONOMOUS — AI-led first round</option>
          </select>
          <p className="text-xs text-slate-500 mt-1">
            {form.sessionType === 'AUTONOMOUS'
              ? 'Creates an application and autonomous session; the candidate receives an invite email with join instructions.'
              : 'Creates a standard scheduled interview on the calendar.'}
          </p>
        </div>

        {form.sessionType === 'SUPERVISED' && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Title <span className="text-slate-500 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Senior Frontend Engineer Round 2"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="input w-full"
          />
        </div>
        )}

        {form.sessionType === 'SUPERVISED' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Host name <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              placeholder="Jane Smith"
              value={form.hostName}
              onChange={(e) => handleChange('hostName', e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Host email <span className="text-slate-500 font-normal">(from JWT)</span>
            </label>
            <input
              type="email"
              placeholder="interviewer@company.com"
              value={form.hostEmail}
              onChange={(e) => handleChange('hostEmail', e.target.value)}
              className="input w-full"
            />
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Candidate name <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              placeholder="John Doe"
              value={form.candidateName}
              onChange={(e) => handleChange('candidateName', e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Candidate email <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              required
              placeholder="candidate@email.com"
              value={form.candidateEmail}
              onChange={(e) => handleChange('candidateEmail', e.target.value)}
              className="input w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className={availabilityConfigured ? 'sm:col-span-2' : ''}>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Scheduled at <span className="text-red-600">*</span>
            </label>
            {availabilityConfigured === null && slotsLoading ? (
              <p className="text-sm text-slate-500">Checking host availability…</p>
            ) : availabilityConfigured ? (
              <div className="space-y-3">
                <input
                  type="date"
                  required
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="input w-full max-w-xs"
                />
                {slotsLoading ? (
                  <p className="text-sm text-slate-500">Loading open slots…</p>
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    No open slots on this day. Pick another date or ask the host to update their availability.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableSlots.map((slot) => {
                      const selected = selectedSlotIso === slot;
                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setSelectedSlotIso(slot)}
                          className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                            selected
                              ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-teal-300'
                          }`}
                        >
                          {formatSlotLabel(slot)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <input
                type="datetime-local"
                required
                value={form.scheduledAt}
                onChange={(e) => handleChange('scheduledAt', e.target.value)}
                className="input w-full"
              />
            )}
          </div>
          {/* CR-011: Duration used to render ONLY on the SUPERVISED branch - the
              AI-led path showed Job title in this slot and never collected a
              duration, so every autonomous interview silently used the entity
              default of 60. Job title now has its own row below and Duration is
              always present. */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Duration (minutes)
            </label>
            <input
              type="number"
              min="15"
              max="480"
              value={form.durationMinutes}
              onChange={(e) => handleChange('durationMinutes', e.target.value)}
              className="input w-full"
            />
            {form.sessionType === 'AUTONOMOUS' && (
              <p className="mt-1 text-xs text-slate-500">
                Aria starts wrapping up at 80% of this budget and stops at 120%.
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="schedule-interview-language" className="block text-sm font-medium text-slate-700 mb-1.5">
            Interview language
          </label>
          <select
            id="schedule-interview-language"
            value={form.language}
            onChange={(e) => handleChange('language', e.target.value)}
            className="input w-full"
            style={{ maxWidth: '16rem' }}
          >
            {INTERVIEW_LANGUAGES.map(({ code, label }) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            Aria conducts the live voice interview in this language. Grading and feedback emails stay in English.
          </p>
        </div>

        {form.sessionType === 'AUTONOMOUS' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Job title <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Senior Frontend Engineer"
              value={form.jobTitle}
              onChange={(e) => handleChange('jobTitle', e.target.value)}
              className="input w-full"
            />
          </div>
        )}

        {error && (
          <p className="text-red-600 text-sm" role="alert">{error}</p>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Question set
            {form.sessionType === 'AUTONOMOUS' ? (
              <span className="text-red-600"> *</span>
            ) : (
              <span className="text-slate-500 font-normal"> (optional — host can pick at session start)</span>
            )}
          </label>
          <select
            value={form.questionSetId}
            onChange={(e) => handleChange('questionSetId', e.target.value)}
            className="input w-full"
            required={form.sessionType === 'AUTONOMOUS'}
          >
            <option value="">
              {form.sessionType === 'AUTONOMOUS' ? 'Select a question set…' : '- No preselected set -'}
            </option>
            {questionSets.filter((qs) => form.sessionType !== 'AUTONOMOUS' || (selectedOrgId && (qs.orgId == null || qs.orgId === selectedOrgId))).map((qs) => (
              <option key={qs.id} value={qs.id}>
                {qs.name || qs.title || qs.id}
              </option>
            ))}
          </select>
        </div>

        {form.sessionType === 'AUTONOMOUS' && (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/80 p-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.codingRoundEnabled}
                onChange={(e) => handleChange('codingRoundEnabled', e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-teal-600"
              />
              <span className="text-sm text-slate-700">
                Include coding round after interview
              </span>
            </label>
            {form.codingRoundEnabled && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Coding round duration (minutes)
                </label>
                <select
                  value={form.codingDurationMinutes}
                  onChange={(e) => handleChange('codingDurationMinutes', e.target.value)}
                  className="input w-full"
                  style={{ maxWidth: '12rem' }}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary inline-flex items-center gap-2 px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {form.sessionType === 'AUTONOMOUS' ? 'Creating…' : 'Scheduling...'}
              </>
            ) : (
              form.sessionType === 'AUTONOMOUS' ? 'Create AI interview' : 'Schedule interview'
            )}
          </button>
          <button
            type="button"
            onClick={() => onCancel?.()}
            className="btn btn-secondary px-4 py-2.5 text-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
